import { useState, useEffect } from 'react';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconRefresh,
  IconX,
  IconDeviceFloppy,
  IconLoader2,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';

interface Product {
  id?: number;
  whitelabel?: string;
  monday_id?: string | null;
  name?: string;
  description?: string;
  promo_text?: string;
  usage_text_data?: string;
  usage_text_voice?: string | null;
  usage_text_sms?: string | null;
  code?: string;
  barcode?: string | null;
  category?: string;
  sub_category?: string;
  type?: string | null;
  logo?: string | null;
  xero_item_code?: string | null;
  xero_gl_code?: string | null;
  product_type_id?: number;
  activation_fee?: number;
  activation_fee_minimum?: number | null;
  activation_fee_maximum?: number | null;
  activation_fee_increments?: number | null;
  sale_price?: number;
  airtime_volume?: number;
  data_volume?: number;
  voice_volume?: number | null;
  sms_volume?: number | null;
  in_app_extra?: number;
  special?: number;
  in_app_extra_percentage?: number;
  expiry?: number;
  expiry_period?: number;
  add_on_priority?: number;
  i_dest_group?: number;
  product_key?: string | null;
  product_value?: string | null;
  is_active?: number;
  sort_order?: number;
  expiry_date?: string;
  discounted?: number;
  created_at?: string;
  updated_at?: string;
}

const productDetailGroups: Record<
  string,
  { key: keyof Product; label: string; type?: 'text' | 'number' | 'boolean' | 'select' | 'date'; options?: string[]; multiline?: boolean; hasConfig?: boolean }[]
> = {
  'Basic Information': [
    { key: 'name', label: 'Product Name', type: 'text' },
    { key: 'whitelabel', label: 'White Label', type: 'select', hasConfig: true },
    { key: 'code', label: 'Code', type: 'text' },
    { key: 'description', label: 'Description', type: 'text', multiline: true },
    { key: 'promo_text', label: 'Promo Text', type: 'text' },
    { key: 'barcode', label: 'Barcode', type: 'text' },
  ],
  'Category & Type': [
    { key: 'category', label: 'Category', type: 'select', hasConfig: true },
    { key: 'sub_category', label: 'Sub Category', type: 'select', hasConfig: true },
    { key: 'type', label: 'Type', type: 'select', hasConfig: true },
    { key: 'product_type_id', label: 'Product Type ID', type: 'select', hasConfig: true },
  ],
  'Usage Text': [
    { key: 'usage_text_data', label: 'Usage Text Data', type: 'text' },
    { key: 'usage_text_voice', label: 'Usage Text Voice', type: 'text' },
    { key: 'usage_text_sms', label: 'Usage Text SMS', type: 'text' },
  ],
  'Pricing': [
    { key: 'activation_fee', label: 'Activation Fee', type: 'number' },
    { key: 'activation_fee_minimum', label: 'Activation Fee Minimum', type: 'number' },
    { key: 'activation_fee_maximum', label: 'Activation Fee Maximum', type: 'number' },
    { key: 'activation_fee_increments', label: 'Activation Fee Increments', type: 'number' },
    { key: 'sale_price', label: 'Sale Price', type: 'number' },
    { key: 'in_app_extra', label: 'In App Extra', type: 'number' },
    { key: 'in_app_extra_percentage', label: 'In App Extra Percentage', type: 'number' },
    { key: 'discounted', label: 'Discounted', type: 'boolean' },
  ],
  'Volume': [
    { key: 'airtime_volume', label: 'Airtime Volume', type: 'number' },
    { key: 'data_volume', label: 'Data Volume', type: 'number' },
    { key: 'voice_volume', label: 'Voice Volume', type: 'number' },
    { key: 'sms_volume', label: 'SMS Volume', type: 'number' },
  ],
  'Expiry & Priority': [
    { key: 'expiry', label: 'Expiry', type: 'number' },
    { key: 'expiry_period', label: 'Expiry Period (hours)', type: 'number' },
    { key: 'expiry_date', label: 'Expiry Date', type: 'date' },
    { key: 'add_on_priority', label: 'Add On Priority', type: 'number' },
  ],
  'Integration': [
    { key: 'xero_item_code', label: 'Xero Item Code', type: 'text' },
    { key: 'xero_gl_code', label: 'Xero GL Code', type: 'text' },
    { key: 'monday_id', label: 'Monday.com ID', type: 'text' },
    { key: 'logo', label: 'Logo URL', type: 'text' },
  ],
  'Product Configuration': [
    { key: 'product_key', label: 'Product Key', type: 'text' },
    { key: 'product_value', label: 'Product Value', type: 'text' },
    { key: 'i_dest_group', label: 'Destination Group', type: 'select', hasConfig: true },
  ],
  'Status & Display': [
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
    { key: 'special', label: 'Special', type: 'boolean' },
    { key: 'sort_order', label: 'Sort Order', type: 'number' },
  ],
};

const BOOLEAN_FIELDS = new Set<keyof Product>(['is_active', 'special', 'discounted']);
const NUMERIC_FIELDS = new Set<keyof Product>([
  'product_type_id',
  'activation_fee',
  'activation_fee_minimum',
  'activation_fee_maximum',
  'activation_fee_increments',
  'sale_price',
  'airtime_volume',
  'data_volume',
  'voice_volume',
  'sms_volume',
  'in_app_extra',
  'in_app_extra_percentage',
  'expiry',
  'expiry_period',
  'add_on_priority',
  'i_dest_group',
  'sort_order',
]);

const NONE_VALUE = '__none__';

export function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const [formData, setFormData] = useState<Partial<Product>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const [fieldValues, setFieldValues] = useState<Record<string, any[]>>({});
  const [loadingConfig, setLoadingConfig] = useState(false);

  const [subCategoryValues, setSubCategoryValues] = useState<any[]>([]);
  const [typeValues, setTypeValues] = useState<any[]>([]);
  const [productTypeIdValues, setProductTypeIdValues] = useState<any[]>([]);

  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingProductTypeIds, setLoadingProductTypeIds] = useState(false);

  useEffect(() => {
    loadProducts();
    loadFieldValues();
  }, []);

  const loadFieldValues = async () => {
    setLoadingConfig(true);
    try {
      const res = await api.get('/products/config/field-values');
      setFieldValues(res.data || {});
    } catch (err: any) {
      console.error('Failed to load field values:', err);
      setError('Failed to load field configuration values');
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/products');
      setProducts(res.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDrawer = (product?: Product) => {
    if (product && product.id) {
      setSelectedProductId(product.id);
      setSelectedProduct(product);
      setIsCreating(false);
      setFormData(product);
      setActiveStep(0);

      if (product.category) {
        loadSubCategories(product.category);
      }

      if (product.sub_category) {
        loadTypes(product.sub_category, 'sub_category');
      } else if (product.category) {
        loadTypes(product.category, 'category');
      }

      if (product.type) {
        loadProductTypeIds(product.type, 'type');
      } else if (product.sub_category) {
        loadProductTypeIds(product.sub_category, 'sub_category');
      } else if (product.category) {
        loadProductTypeIds(product.category, 'category');
      }
    } else {
      setSelectedProductId(0);
      setSelectedProduct(null);
      setIsCreating(true);
      setFormData({});
      setActiveStep(0);
      setSubCategoryValues([]);
      setTypeValues([]);
      setProductTypeIdValues([]);
    }
    setSaveError('');
    setSaveSuccess('');
  };

  const handleCloseDrawer = () => {
    setSelectedProductId(null);
    setSelectedProduct(null);
    setIsCreating(false);
    setFormData({});
    setActiveStep(0);
    setSaveError('');
    setSaveSuccess('');
    setSubCategoryValues([]);
    setTypeValues([]);
    setProductTypeIdValues([]);
  };

  const handleFieldChange = (field: keyof Product, value: any) => {
    const newFormData = {
      ...formData,
      [field]: value === '' ? undefined : value,
    };

    if (field === 'category') {
      newFormData.sub_category = undefined;
      newFormData.type = undefined;
      newFormData.product_type_id = undefined;
      setSubCategoryValues([]);
      setTypeValues([]);
      setProductTypeIdValues([]);

      if (value && value !== '') {
        loadSubCategories(value);
      }
    } else if (field === 'sub_category') {
      newFormData.type = undefined;
      newFormData.product_type_id = undefined;
      setTypeValues([]);
      setProductTypeIdValues([]);

      if (value && value !== '') {
        loadTypes(value, 'sub_category');
      } else if (formData.category) {
        loadTypes(formData.category, 'category');
      }
    } else if (field === 'type') {
      newFormData.product_type_id = undefined;
      setProductTypeIdValues([]);

      if (value && value !== '') {
        loadProductTypeIds(value, 'type');
      } else {
        const filterSubCategory = newFormData.sub_category || formData.sub_category;
        const filterCategory = newFormData.category || formData.category;

        if (filterSubCategory) {
          loadProductTypeIds(filterSubCategory, 'sub_category');
        } else if (filterCategory) {
          loadProductTypeIds(filterCategory, 'category');
        }
      }
    }

    setFormData(newFormData);
  };

  const loadSubCategories = async (category: string) => {
    setLoadingSubCategories(true);
    try {
      const res = await api.get(
        `/products/config/field-values/sub_category?filterBy=category&filterValue=${encodeURIComponent(category)}`
      );
      setSubCategoryValues(res.data || []);
    } catch (err: any) {
      console.error('Failed to load sub-categories:', err);
      setSubCategoryValues([]);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  const loadTypes = async (filterValue: string, filterBy: 'category' | 'sub_category') => {
    setLoadingTypes(true);
    try {
      const res = await api.get(
        `/products/config/field-values/type?filterBy=${filterBy}&filterValue=${encodeURIComponent(filterValue)}`
      );
      setTypeValues(res.data || []);
    } catch (err: any) {
      console.error('Failed to load types:', err);
      setTypeValues([]);
    } finally {
      setLoadingTypes(false);
    }
  };

  const loadProductTypeIds = async (filterValue: string | number, filterBy: 'category' | 'sub_category' | 'type') => {
    setLoadingProductTypeIds(true);
    try {
      const res = await api.get(
        `/products/config/field-values/product_type_id?filterBy=${filterBy}&filterValue=${encodeURIComponent(String(filterValue))}`
      );
      setProductTypeIdValues(res.data || []);
    } catch (err: any) {
      console.error('Failed to load product_type_ids:', err);
      setProductTypeIdValues([]);
    } finally {
      setLoadingProductTypeIds(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSaveSuccess('');

    try {
      const dataToSave: any = { ...formData };

      if (isCreating) {
        delete dataToSave.id;
        delete dataToSave.created_at;
        delete dataToSave.updated_at;
      }

      Object.keys(dataToSave).forEach((key) => {
        if (dataToSave[key] === '') {
          dataToSave[key] = null;
        }
      });

      if (isCreating) {
        await api.post('/products', dataToSave);
        setSaveSuccess('Product created successfully');
        setTimeout(() => {
          handleCloseDrawer();
          loadProducts();
        }, 1000);
      } else if (selectedProductId) {
        await api.put(`/products/${selectedProductId}`, dataToSave);
        setSaveSuccess('Product updated successfully');
        loadProducts();
        const updatedProduct = await api.get(`/products/${selectedProductId}`);
        setSelectedProduct(updatedProduct.data);
        setFormData(updatedProduct.data);
      }
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const renderFieldInput = (fieldKey: keyof Product, label: string, type?: string, multiline?: boolean, hasConfig?: boolean) => {
    const value = formData[fieldKey];
    const isBoolean = BOOLEAN_FIELDS.has(fieldKey);
    const isNumeric = NUMERIC_FIELDS.has(fieldKey);
    const isDate = type === 'date';
    const isSelect = type === 'select' || hasConfig;

    let options: any[] = [];
    if (fieldKey === 'sub_category') {
      options = subCategoryValues;
    } else if (fieldKey === 'type') {
      options = typeValues;
    } else if (fieldKey === 'product_type_id') {
      options = productTypeIdValues;
    } else if (hasConfig && fieldValues[fieldKey as string]) {
      options = fieldValues[fieldKey as string];
    }

    if (isBoolean) {
      const boolValue = Boolean(Number(value ?? 0));
      return (
        <div className="flex items-center gap-3 py-4">
          <Switch
            id={`switch-${String(fieldKey)}`}
            checked={boolValue}
            onCheckedChange={(checked) => handleFieldChange(fieldKey, checked ? 1 : 0)}
          />
          <Label htmlFor={`switch-${String(fieldKey)}`}>{label}</Label>
        </div>
      );
    }

    if (isDate) {
      const dateValue = value
        ? (typeof value === 'string' ? value.split(' ')[0] : value)
        : '';
      return (
        <div className="space-y-2">
          <Label htmlFor={`date-${String(fieldKey)}`}>{label}</Label>
          <Input
            id={`date-${String(fieldKey)}`}
            type="date"
            value={dateValue as string}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value ? `${e.target.value} 00:00:00` : null)}
          />
        </div>
      );
    }

    if (isSelect) {
      const stringValue = value !== null && value !== undefined ? String(value) : '';
      const isNumericSelect = isNumeric;
      const isSubCategory = fieldKey === 'sub_category';
      const isType = fieldKey === 'type';
      const isProductTypeId = fieldKey === 'product_type_id';

      let isDisabled = false;
      if (isSubCategory && !formData.category) {
        isDisabled = true;
      } else if (isType) {
        isDisabled = !formData.sub_category && !formData.category;
      } else if (isProductTypeId) {
        isDisabled = !formData.type && !formData.sub_category && !formData.category;
      }

      let isLoading = false;
      if (isSubCategory) {
        isLoading = loadingSubCategories;
      } else if (isType) {
        isLoading = loadingTypes;
      } else if (isProductTypeId) {
        isLoading = loadingProductTypeIds;
      }

      const hasOptions = options.length > 0;

      let helpMessage = 'No options available';
      if (isSubCategory && !formData.category) {
        helpMessage = 'Select a category first';
      } else if (isType && !formData.sub_category && !formData.category) {
        helpMessage = 'Select a category first';
      } else if (isType && formData.sub_category) {
        helpMessage = 'No types found for this sub-category';
      } else if (isType && formData.category) {
        helpMessage = 'No types found for this category';
      } else if (isProductTypeId && !formData.type && !formData.sub_category && !formData.category) {
        helpMessage = 'Select a category first';
      } else if (isProductTypeId && formData.type) {
        helpMessage = 'No product type IDs found for this type';
      } else if (isProductTypeId && formData.sub_category) {
        helpMessage = 'No product type IDs found for this sub-category';
      } else if (isProductTypeId && formData.category) {
        helpMessage = 'No product type IDs found for this category';
      }

      const displayOptions = [...options];
      if (stringValue && !displayOptions.some((o) => String(o) === stringValue)) {
        displayOptions.unshift(isNumericSelect ? Number(stringValue) : stringValue);
      }

      return (
        <div className="space-y-2">
          <Label>{label}</Label>
          <Select
            value={stringValue || NONE_VALUE}
            onValueChange={(val) => {
              const actualVal = val === NONE_VALUE ? '' : val;
              if (isNumericSelect) {
                handleFieldChange(fieldKey, actualVal === '' ? null : (isNaN(Number(actualVal)) ? null : Number(actualVal)));
              } else {
                handleFieldChange(fieldKey, actualVal === '' ? null : actualVal);
              }
            }}
            disabled={isDisabled || isLoading}
          >
            <SelectTrigger className="min-w-[200px]">
              <SelectValue placeholder={label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>None</SelectItem>
              {isLoading ? (
                <SelectItem value="__loading__" disabled>Loading...</SelectItem>
              ) : hasOptions || displayOptions.length > 0 ? (
                displayOptions.map((option: any) => (
                  <SelectItem key={String(option)} value={String(option)}>
                    {String(option)}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="__empty__" disabled>{helpMessage}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (multiline) {
      return (
        <div className="space-y-2">
          <Label htmlFor={`textarea-${String(fieldKey)}`}>{label}</Label>
          <Textarea
            id={`textarea-${String(fieldKey)}`}
            value={value !== null && value !== undefined ? String(value) : ''}
            rows={4}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
          />
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <Label htmlFor={`input-${String(fieldKey)}`}>{label}</Label>
        <Input
          id={`input-${String(fieldKey)}`}
          type={isNumeric ? 'number' : 'text'}
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => {
            const val = e.target.value;
            if (isNumeric) {
              if (val === '') {
                handleFieldChange(fieldKey, null);
              } else {
                const numVal = parseFloat(val);
                handleFieldChange(fieldKey, isNaN(numVal) ? null : numVal);
              }
            } else {
              handleFieldChange(fieldKey, val);
            }
          }}
        />
      </div>
    );
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    setLoading(true);
    setError('');
    try {
      await api.delete(`/products/${id}`);
      setSuccess('Product deleted successfully');
      loadProducts();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete product');
    } finally {
      setLoading(false);
    }
  };

  const steps = Object.entries(productDetailGroups);

  return (
    <div>
      <PageHeader
        title="Products Management"
        subtitle="Manage product catalog and inventory"
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={loadProducts}
              disabled={loading}
            >
              <IconRefresh className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={() => handleOpenDrawer()}>
              <IconPlus className="mr-2 h-4 w-4" />
              Add Product
            </Button>
          </div>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-3">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-6">
          {loading && products.length === 0 ? (
            <div className="flex justify-center p-8">
              <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : products.length === 0 ? (
            <Alert variant="info">
              <AlertDescription>
                No products found. Click &quot;Add Product&quot; to create one.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-bold">ID</TableHead>
                    <TableHead className="font-bold">Code</TableHead>
                    <TableHead className="font-bold">Name</TableHead>
                    <TableHead className="font-bold">Category</TableHead>
                    <TableHead className="font-bold">Sub Category</TableHead>
                    <TableHead className="font-bold">Sale Price</TableHead>
                    <TableHead className="font-bold">Data Volume</TableHead>
                    <TableHead className="font-bold">Active</TableHead>
                    <TableHead className="text-right font-bold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id} className="hover:bg-muted/50">
                      <TableCell>{product.id}</TableCell>
                      <TableCell>{product.code || '-'}</TableCell>
                      <TableCell>{product.name || '-'}</TableCell>
                      <TableCell>{product.category || '-'}</TableCell>
                      <TableCell>{product.sub_category || '-'}</TableCell>
                      <TableCell>
                        {product.sale_price !== null && product.sale_price !== undefined
                          ? `R${product.sale_price.toFixed(2)}`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {product.data_volume !== null && product.data_volume !== undefined
                          ? `${(product.data_volume / 1000).toFixed(1)}GB`
                          : '-'}
                      </TableCell>
                      <TableCell>{product.is_active ? 'Yes' : 'No'}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDrawer(product)}
                          className="text-primary"
                        >
                          <IconEdit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.id!)}
                          className="text-destructive"
                        >
                          <IconTrash className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={selectedProductId !== null} onOpenChange={(open) => { if (!open) handleCloseDrawer(); }}>
        <SheetContent side="right" className="sm:max-w-[85vw] md:max-w-[75vw] flex flex-col gap-0 p-0">
          <div className="px-6 pt-6 pb-4">
            <SheetHeader className="text-left">
              <SheetTitle>
                {isCreating ? 'Add New Product' : selectedProduct?.name || 'Product Details'}
              </SheetTitle>
              {selectedProduct && (
                <p className="text-sm text-muted-foreground">
                  Code: {selectedProduct.code || '-'}
                </p>
              )}
            </SheetHeader>
          </div>
          <Separator />

          <div className="flex-1 overflow-y-auto p-6">
            {saveError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription className="flex items-center justify-between">
                  <span>{saveError}</span>
                  <button onClick={() => setSaveError('')}>
                    <IconX className="h-4 w-4" />
                  </button>
                </AlertDescription>
              </Alert>
            )}
            {saveSuccess && (
              <Alert variant="success" className="mb-4">
                <AlertDescription className="flex items-center justify-between">
                  <span>{saveSuccess}</span>
                  <button onClick={() => setSaveSuccess('')} className="text-current">
                    <IconX className="h-4 w-4" />
                  </button>
                </AlertDescription>
              </Alert>
            )}

            {selectedProduct && !isCreating && (
              <Alert variant="info" className="mb-4">
                <AlertDescription>
                  ID: <strong>{selectedProduct.id}</strong> &bull; Created:{' '}
                  {selectedProduct.created_at
                    ? new Date(selectedProduct.created_at).toLocaleString()
                    : '-'}{' '}
                  &bull; Updated:{' '}
                  {selectedProduct.updated_at
                    ? new Date(selectedProduct.updated_at).toLocaleString()
                    : '-'}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-2 mb-4">
              <Button variant="outline" onClick={handleCloseDrawer} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !formData.name}>
                {saving ? (
                  <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <IconDeviceFloppy className="mr-2 h-4 w-4" />
                )}
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>

            {/* Step navigation */}
            <div className="flex gap-1 overflow-x-auto pb-3 mb-4">
              {steps.map(([label], index) => (
                <button
                  key={label}
                  onClick={() => setActiveStep(index)}
                  className={cn(
                    'px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors',
                    activeStep === index
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Current step content */}
            {(() => {
              const [groupName, fields] = steps[activeStep] || steps[0];
              return (
                <div>
                  <h3 className="text-base font-semibold mb-4">{groupName}</h3>
                  <div className="grid grid-cols-12 gap-6">
                    {fields.map(({ key, label, type, multiline, hasConfig }) => {
                      let colSpan: string;
                      if (multiline) {
                        colSpan = 'col-span-12';
                      } else if (key === 'name') {
                        colSpan = 'col-span-12 md:col-span-8';
                      } else {
                        colSpan = 'col-span-12 sm:col-span-6 md:col-span-4';
                      }
                      return (
                        <div className={colSpan} key={key as string}>
                          {renderFieldInput(key, label, type, multiline, hasConfig)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Step controls */}
            {(() => {
              const isFirst = activeStep === 0;
              const isLast = activeStep === steps.length - 1;
              return (
                <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mt-6">
                  <div>
                    <Button
                      variant="outline"
                      onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                      disabled={isFirst || saving}
                    >
                      Back
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    {!isLast && (
                      <Button
                        onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
                        disabled={saving || (!formData.name && activeStep === 0)}
                      >
                        Next
                      </Button>
                    )}
                    {isLast && (
                      <Button
                        onClick={handleSave}
                        disabled={saving || !formData.name}
                      >
                        {saving ? (
                          <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <IconDeviceFloppy className="mr-2 h-4 w-4" />
                        )}
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
